import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription, switchMap } from 'rxjs';
import { SiteSummaryContext } from '../../../../shared/contexts/site-summary.context';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ClusterResult, SiteResult } from '../../../../shared/models/generated/ziroModels';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ZiroModalPromptModalOptions } from '../../../../modals/prompt-modal/prompt-modal.component';
import { SitesResource } from '../services/sites.resource';
import { tap } from 'rxjs/operators';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

interface SiteListRow extends DatatableRow {
  id: string;
  name: string;
  devicePool: string;
}

@Component({
  selector: 'ziro-sites-list',
  templateUrl: './sites-list.component.html',
  standalone: false,
})
export class SitesListComponent implements OnInit, OnDestroy {
  private siteSummaryContext = inject(SiteSummaryContext);
  private sitesResource = inject(SitesResource);
  private breadcrumbsService = inject(BreadcrumbsService);
  private bottomNavService = inject(BottomNavService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private ziroModalService = inject(ZiroModalService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  clusterId: number;
  isLoading = true;
  tableDataAutomation = 'site-list-table';
  columns: DatatableColumn<SiteListRow>[] = [
    {
      name: 'id',
      columnSize: 'col-sm-1',
      label: 'tkey;admin.site_management.site.id',
    },
    {
      name: 'name',
      columnSize: 'col-sm-4',
      label: 'tkey;admin.site_management.site.site',
    },
    {
      name: 'devicePool',
      columnSize: 'col-sm-5',
      label: 'tkey;admin.site_management.site.device_pool',
    },
  ];
  data: SiteListRow[] = [];

  private _subscription = new Subscription();

  ngOnInit(): void {
    this._initBottomNav();

    this.clusterId = Number(this.route.snapshot.params.clusterId);

    const siteSummarySub = this.siteSummaryContext
      .refreshSiteSummary()
      .pipe(switchMap(() => this.siteSummaryContext.state$))
      .subscribe((siteSummary) => {
        const cluster = siteSummary.clusters.find((cluster) => cluster.id === this.clusterId);
        this.breadcrumbsService.updateBreadcrumbs([
          {
            label: 'tkey;admin.site_management.cluster_sites.header',
            url: '/admin/site-management/clusters',
            routerLink: true,
          },
          {
            label: this.translateService.instant('tkey;admin.site_management.site.header', {
              labelParam: cluster.name,
            }),
          },
        ]);
        this._buildTableRows(cluster);
        this.isLoading = false;
      });
    this._subscription.add(siteSummarySub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  private _buildTableRows(cluster: ClusterResult): void {
    this.data = cluster.sites.map(
      (site): SiteListRow => ({
        id: site.id.toString(),
        name: site.name,
        devicePool: site.devicePools.find((dpResult) => dpResult.serviceName === 'Desk Phone')?.devicePool || '',
        actions: [
          {
            dataAutomation: 'sites-list-edit-button',
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.NEXT,
            onClick: () => {
              this.bottomNavService.dispatch(new BottomNavClearButtonsList());
              this.router.navigate([site.id], { relativeTo: this.route });
            },
          },
          {
            dataAutomation: 'sites-list-delete-button',
            style: ButtonStyles.OUTLINE_DANGER,
            type: ButtonTypes.BUTTON,
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => {
              this._openDeleteSiteModal(site);
            },
          },
          {
            dataAutomation: 'sites-list-copy-button',
            style: ButtonStyles.OUTLINE_DEFAULT,
            type: ButtonTypes.LINK,
            href: window.location.href + `/${site.id}/copy`,
            icon: ZiroIcons.COPY,
          },
        ],
      }),
    );
  }

  private _initBottomNav(): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'bulk-field-copy',
          label: 'tkey;admin.site_management.bulk_change.button',
          cb: () => {
            this.router.navigate(['bulk-copy'], { relativeTo: this.route });
          },
          dataAutomation: 'sites-list-bulk-copy-button',
          buttonClass: ButtonStyles.DEFAULT,
        },
        {
          id: 'add-site',
          label: 'tkey;admin.site_management.add_site.button',
          icon: ZiroIcons.ADD,
          cb: () => {
            this.bottomNavService.dispatch(new BottomNavClearButtonsList());
            this.router.navigate(['new'], { relativeTo: this.route });
          },
          dataAutomation: 'sites-list-add-button',
          buttonClass: ButtonStyles.PRIMARY,
        },
      ]),
    );
  }

  private _openDeleteSiteModal(site: SiteResult): void {
    const options: ZiroModalPromptModalOptions = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        promptBody: this.translateService.instant('tkey;admin.sites.delete.confirm.text', {
          name: site.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'prompt-modal-cancel',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'prompt-modal-confirm',
            cb: () =>
              this.sitesResource.delete(this.clusterId, site.id).pipe(
                tap(() => {
                  this.toastService.push(
                    ToastTypes.INFO,
                    ZiroIcons.DELETE_SMALL,
                    'tkey;admin.sites.delete.toast.title',
                    'tkey;admin.sites.delete.toast.message',
                    { siteName: site.name },
                    null,
                    'text-danger',
                  );
                }),
                switchMap(() => this.siteSummaryContext.refreshSiteSummary()),
              ),
          },
        ],
      },
    };

    this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }
}
